import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Project, ProjectContextType } from '@/types';
import { projectsApi } from '@/utils/api';

const ProjectContext = createContext<ProjectContextType | null>(null);

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectsApi.getAll();
      if (response.data.success) {
        setProjects(response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to load projects');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load projects';
      setError(errorMessage);
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (name: string): Promise<Project> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectsApi.create(name);
      if (response.data.success) {
        const newProject = response.data.data;
        setProjects(prev => [newProject, ...prev]);
        return newProject;
      } else {
        throw new Error(response.data.error || 'Failed to create project');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create project';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectsApi.delete(id);
      if (response.data.success) {
        setProjects(prev => prev.filter(p => p.id !== id));
        if (currentProject?.id === id) {
          setCurrentProject(null);
        }
      } else {
        throw new Error(response.data.error || 'Failed to delete project');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete project';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refreshProjects = async () => {
    await loadProjects();
  };

  const contextValue: ProjectContextType = {
    projects,
    currentProject,
    setCurrentProject,
    createProject,
    deleteProject,
    refreshProjects,
    loading,
    error,
  };

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};